import { safeParseInt } from "@/shared/lib/number";
import { InputAdornment } from "@mui/material";
import { type ForwardedRef, forwardRef, useEffect, useState } from "react";
import type { Merge } from "type-fest";
import { BaseTextField, type BaseTextFieldProps } from "../base-text-field";

export type BaseNumberFieldProps = Merge<
  BaseTextFieldProps,
  {
    value: number | "" | undefined;
    startAdornment?: string;
    onChange?: (value: number | "") => void;
  }
>;
export const BaseNumberField = forwardRef(
  (props: BaseNumberFieldProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      onChange,
      onBlur,
      sx,
      startAdornment,
      value: sourceValue,
      ...rest
    } = props;
    const [rawValue, setRawValue] = useState<string>();
    useEffect(() => {
      setRawValue(sourceValue?.toString() ?? "");
    }, [sourceValue]);
    return (
      <BaseTextField
        ref={ref}
        value={rawValue}
        slotProps={{
          input: {
            inputMode: "numeric",
            startAdornment: startAdornment ? (
              <InputAdornment position="start">{startAdornment}</InputAdornment>
            ) : undefined,
          },
        }}
        sx={{
          "& input[type=number]": {
            MozAppearance: "textfield", // Firefox用
          },
          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
            {
              WebkitAppearance: "none", // Chrome, Safari用
              margin: 0,
            },
          ...sx,
        }}
        onChange={(e) => {
          const filteredValue = e.target.value.replace(/[^0-9]/g, "");
          setRawValue(filteredValue);
        }}
        onBlur={(e) => {
          const parsedValue = safeParseInt(e.target.value, 10);
          setRawValue(parsedValue?.toString() ?? "");
          if (parsedValue) {
            onChange?.(parsedValue);
          }
          onBlur?.(e);
        }}
        {...rest}
      />
    );
  },
);
